class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const updateEvent = FacetFiltersForm.startUpdateEvent(searchParams);
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, updateEvent)
        : FacetFiltersForm.renderSectionFromFetch(url, event, updateEvent);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static startUpdateEvent(searchParams) {
    const { SearchUpdateEvent, CollectionUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent && !CollectionUpdateEvent) return;

    const facetsForm = document.querySelector('facet-filters-form');
    const facetsContainer = document.querySelector('.facets-container');
    const collectionId = facetsContainer?.dataset.collectionId || null;
    const collectionHandle = facetsContainer?.dataset.collectionHandle || '';
    const currentCount = parseInt(document.getElementById('ProductCount')?.dataset.productCount) || 0;
    const urlSearchParams = new URLSearchParams(searchParams);
    const isSearchPage = facetsContainer?.dataset.template === 'search';
    const isCollectionPage = facetsContainer?.dataset.template === 'collection';
    const dispatchTarget = facetsForm || document;

    let deferred;

    if (isSearchPage) {
      deferred = SearchUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new SearchUpdateEvent({
          search: {
            query: urlSearchParams.get('q') || '',
            productFilters: SearchUpdateEvent.parseProductFilters(urlSearchParams),
            sortKey: SearchUpdateEvent.getSortKey(urlSearchParams),
          },
          promise: deferred.promise,
        }),
      );
    } else if (isCollectionPage) {
      // collectionId is null for auto-generated collections (/collections/all,
      // /collections/vendors/<x>, etc.) — the SSE schema accepts that.
      deferred = CollectionUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new CollectionUpdateEvent({
          collection: { id: collectionId, handle: collectionHandle, productsCount: currentCount },
          productFilters: CollectionUpdateEvent.parseProductFilters(urlSearchParams),
          sortKey: CollectionUpdateEvent.getSortKey(urlSearchParams),
          promise: deferred.promise,
        }),
      );
    }

    if (!deferred) return;

    return {
      resolve: (filteredCount) => {
        deferred.resolve({ [isSearchPage ? 'totalCount' : 'productsCount']: filteredCount });
      },
      reject: deferred.reject,
    };
  }

  static renderSectionFromFetch(url, event, updateEvent) {
    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderSection(html, event, updateEvent);
      })
      .catch((error) => {
        console.error(error);
        updateEvent?.reject(error);
      });
  }

  static renderSectionFromCache(filterDataUrl, event, updateEvent) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderSection(html, event, updateEvent);
  }

  static renderSection(html, event, updateEvent) {
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html, updateEvent);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
    if (window.KKSearchCustomSort) window.KKSearchCustomSort.refresh();
    if (window.KKCollectionAvailabilitySort) window.KKCollectionAvailabilitySort.refresh();
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html, updateEvent) {
    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsedHtml.getElementById('ProductCount');
    const count = sourceCount.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.dataset.productCount = sourceCount.dataset.productCount || '';
    container.dataset.totalCount = sourceCount.dataset.totalCount || '';
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));

    updateEvent?.resolve(parseInt(sourceCount.dataset.productCount) || 0);
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (!isTextInput) {
          // Try to return focus to the same checkbox the user just toggled,
          // re-selecting it from the freshly rendered HTML by its id.
          const originatingInputId = event.target.id;
          const matchingInput = originatingInputId
            ? newFacetDetailsElement.querySelector(`#${CSS.escape(originatingInputId)}`)
            : null;

          if (matchingInput) {
            matchingInput.focus();
          } else {
            // Fallback to summary/close button if the checkbox can't be found
            const fallbackSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
              ? `.mobile-facets__close-button`
              : `.facets__summary`;
            const fallbackElement = newFacetDetailsElement.querySelector(fallbackSelector);
            if (fallbackElement) fallbackElement.focus();
          }
        }
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    if (window.KKSearchCustomSort) {
      searchParams = window.KKSearchCustomSort.normalizeSearchParams(searchParams);
    }
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    const normalizedUrl = window.KKSearchCustomSort
      ? window.KKSearchCustomSort.normalizeSearchParams(url)
      : url;
    FacetFiltersForm.renderPage(normalizedUrl);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);


/* KupiKocke search sorting extension, v0.3.44
 * Shopify storefront search natively supports relevance + price only.
 * Title/date sorting is therefore applied across ALL matching search-result pages
 * in the browser, with its own pagination. Native relevance/price remain Shopify-driven.
 */
window.KKSearchCustomSort = (() => {
  const customSorts = new Set([
    'manual',
    'best-selling',
    'created-descending',
    'created-ascending',
    'title-ascending',
    'title-descending',
  ]);

  const labels = {
    relevance: 'ustreznosti',
    manual: 'izpostavljenosti',
    'best-selling': 'najbolje prodajano',
    'price-ascending': 'ceni od najnižje naprej',
    'price-descending': 'ceni od najvišje naprej',
    'created-descending': 'vnosu od najnovejše naprej',
    'created-ascending': 'vnosu od najstarejše naprej',
    'title-ascending': 'abecedi od A do Ž',
    'title-descending': 'abecedi od Ž do A',
  };

  const resultCache = new Map();
  const collator = new Intl.Collator('sl-SI', { sensitivity: 'base', numeric: true });
  let refreshSequence = 0;

  function isSearchPage() {
    return window.location.pathname.includes('/search') && document.querySelector('[data-kk-search-sort-control]');
  }

  function getCurrentCustomSort() {
    if (!isSearchPage()) return null;
    const value = new URLSearchParams(window.location.search).get('kk_sort');
    return customSorts.has(value) ? value : null;
  }

  function getCurrentDisplaySort() {
    const params = new URLSearchParams(window.location.search);
    const custom = params.get('kk_sort');
    if (customSorts.has(custom)) return custom;
    const nativeSort = params.get('sort_by');
    return labels[nativeSort] ? nativeSort : 'relevance';
  }

  function getCurrentCustomPage() {
    const page = parseInt(new URLSearchParams(window.location.search).get('kk_page') || '1', 10);
    return Number.isFinite(page) && page > 0 ? page : 1;
  }

  function closeControl(wrapper) {
    if (!wrapper) return;
    wrapper.classList.remove('is-open');
    const menu = wrapper.querySelector('.kk-sort-menu');
    const button = wrapper.querySelector('.kk-sort-trigger');
    if (menu) menu.hidden = true;
    if (button) button.setAttribute('aria-expanded', 'false');
  }

  function updateControls() {
    if (!isSearchPage()) return;
    const activeValue = getCurrentDisplaySort();
    const activeLabel = labels[activeValue] || labels.relevance;

    document.querySelectorAll('[data-kk-search-sort-control]').forEach((wrapper) => {
      const label = wrapper.querySelector('.kk-sort-trigger__label');
      if (label) label.textContent = activeLabel;

      wrapper.querySelectorAll('.kk-sort-option').forEach((option) => {
        const selected = option.dataset.value === activeValue;
        option.classList.toggle('is-selected', selected);
        option.setAttribute('aria-selected', selected ? 'true' : 'false');
      });

      const nativeSelect = wrapper.querySelector('.kk-sort-native');
      if (nativeSelect && !customSorts.has(activeValue)) nativeSelect.value = activeValue;
    });
  }

  function buildStateUrl(params) {
    const query = params.toString();
    return `${window.location.pathname}${query ? `?${query}` : ''}`;
  }

  function syncFacetState(searchParams) {
    if (typeof FacetFiltersForm !== 'undefined') FacetFiltersForm.searchParamsPrev = searchParams;
  }

  function setCustomSort(value) {
    if (!customSorts.has(value) || !isSearchPage()) return;
    const params = new URLSearchParams(window.location.search);
    params.delete('sort_by');
    params.delete('page');
    params.set('kk_sort', value);
    params.set('kk_page', '1');
    const searchParams = params.toString();
    history.pushState({ searchParams }, '', buildStateUrl(params));
    syncFacetState(searchParams);
    updateControls();
    refresh(false);
  }

  function clearCustomSortState() {
    if (!isSearchPage()) return;
    const params = new URLSearchParams(window.location.search);
    if (!params.has('kk_sort') && !params.has('kk_page')) return;
    params.delete('kk_sort');
    params.delete('kk_page');
    params.delete('page');
    const searchParams = params.toString();
    history.replaceState({ searchParams }, '', buildStateUrl(params));
    syncFacetState(searchParams);
  }

  function normalizeSearchParams(searchParams) {
    const custom = getCurrentCustomSort();
    if (!custom) return searchParams;
    const params = new URLSearchParams(searchParams);
    params.delete('sort_by');
    params.delete('page');
    params.set('kk_sort', custom);
    params.set('kk_page', '1');
    return params.toString();
  }

  function getFetchContext() {
    const grid = document.getElementById('product-grid');
    if (!grid) return null;
    const sectionId = grid.dataset.id;
    const totalItems = parseInt(grid.dataset.kkTotalItems || '0', 10);
    const pageSize = parseInt(grid.dataset.kkPageSize || '24', 10) || 24;
    if (!sectionId || !Number.isFinite(totalItems)) return null;

    const baseParams = new URLSearchParams(window.location.search);
    baseParams.delete('kk_sort');
    baseParams.delete('kk_page');
    baseParams.delete('page');
    baseParams.delete('section_id');
    const customSort = new URLSearchParams(window.location.search).get('kk_sort');
    baseParams.set(
      'sort_by',
      customSort === 'manual' || customSort === 'best-selling' || customSort === 'created-descending' ||
      customSort === 'created-ascending' || customSort === 'title-ascending' || customSort === 'title-descending'
        ? customSort
        : 'relevance'
    );

    const cacheParams = new URLSearchParams(baseParams);
    const serverSort = cacheParams.get('sort_by') || 'relevance';
    cacheParams.delete('sort_by');
    const cacheKey = `${window.location.pathname}?${cacheParams.toString()}|sort:${serverSort}|${sectionId}|${totalItems}|${pageSize}`;

    return {
      grid,
      sectionId,
      totalItems,
      pageSize,
      totalPages: Math.max(1, Math.ceil(totalItems / pageSize)),
      baseParams,
      cacheKey,
    };
  }

  async function fetchPage(context, pageNumber) {
    const params = new URLSearchParams(context.baseParams);
    params.set('section_id', context.sectionId);
    params.set('page', String(pageNumber));
    const response = await fetch(`${window.location.pathname}?${params.toString()}`);
    if (!response.ok) throw new Error(`Search sort fetch failed: ${response.status}`);
    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    return Array.from(parsed.querySelectorAll('#product-grid ul.product-grid > li[data-kk-sort-id]')).map((item) => ({
      id: item.dataset.kkSortId || '',
      title: item.dataset.kkSortTitle || '',
      created: parseInt(item.dataset.kkSortCreated || '0', 10) || 0,
      html: item.outerHTML,
    }));
  }

  async function fetchAllResults(context) {
    if (resultCache.has(context.cacheKey)) return resultCache.get(context.cacheKey);

    const all = [];
    const concurrency = 4;
    for (let start = 1; start <= context.totalPages; start += concurrency) {
      const requests = [];
      for (let page = start; page < start + concurrency && page <= context.totalPages; page += 1) {
        requests.push(fetchPage(context, page));
      }
      const batches = await Promise.all(requests);
      batches.forEach((batch) => all.push(...batch));
    }

    const unique = [];
    const seen = new Set();
    all.forEach((item, index) => {
      const key = item.id || `result-${index}`;
      if (seen.has(key)) return;
      seen.add(key);
      unique.push(item);
    });

    resultCache.set(context.cacheKey, unique);
    return unique;
  }

  function sortedCopy(items, sortValue) {
    const sorted = items.slice();
    if (sortValue === 'manual' || sortValue === 'best-selling') {
      return sorted;
    }

    sorted.sort((a, b) => {
      if (sortValue === 'title-ascending' || sortValue === 'title-descending') {
        const result = collator.compare(a.title, b.title);
        return sortValue === 'title-descending' ? -result : result;
      }

      if (!a.created && b.created) return 1;
      if (a.created && !b.created) return -1;
      if (!a.created && !b.created) return collator.compare(a.title, b.title);

      const result = a.created - b.created;
      if (result === 0) return collator.compare(a.title, b.title);
      return sortValue === 'created-descending' ? -result : result;
    });
    return sorted;
  }

  function customPageUrl(pageNumber) {
    const params = new URLSearchParams(window.location.search);
    params.delete('page');
    params.set('kk_page', String(pageNumber));
    return buildStateUrl(params);
  }

  function makeCaret() {
    const wrapper = document.createElement('span');
    wrapper.className = 'svg-wrapper';
    wrapper.innerHTML = '<svg class="icon icon-caret" viewBox="0 0 10 6"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"/></svg>';
    return wrapper;
  }

  function appendPageLink(list, pageNumber, currentPage) {
    const li = document.createElement('li');
    if (pageNumber === currentPage) {
      const current = document.createElement('a');
      current.setAttribute('role', 'link');
      current.setAttribute('aria-disabled', 'true');
      current.setAttribute('aria-current', 'page');
      current.className = 'pagination__item pagination__item--current light';
      current.textContent = String(pageNumber);
      li.appendChild(current);
    } else {
      const link = document.createElement('a');
      link.href = customPageUrl(pageNumber);
      link.className = 'pagination__item link';
      link.dataset.kkCustomPage = String(pageNumber);
      link.setAttribute('aria-label', `Stran ${pageNumber}`);
      link.textContent = String(pageNumber);
      li.appendChild(link);
    }
    list.appendChild(li);
  }

  function appendEllipsis(list) {
    const li = document.createElement('li');
    const span = document.createElement('span');
    span.className = 'pagination__item';
    span.textContent = '…';
    li.appendChild(span);
    list.appendChild(li);
  }

  function renderPagination(container, currentPage, totalPages) {
    const existing = container.querySelector('.pagination-wrapper');
    if (existing) existing.remove();
    if (totalPages <= 1) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'pagination-wrapper';
    wrapper.dataset.page = String(currentPage);
    const nav = document.createElement('nav');
    nav.className = 'pagination';
    nav.setAttribute('role', 'navigation');
    nav.setAttribute('aria-label', 'Strani');
    const list = document.createElement('ul');
    list.className = 'pagination__list list-unstyled';
    list.setAttribute('role', 'list');

    if (currentPage > 1) {
      const li = document.createElement('li');
      const link = document.createElement('a');
      link.href = customPageUrl(currentPage - 1);
      link.className = 'pagination__item pagination__item--next pagination__item-arrow link motion-reduce';
      link.dataset.kkCustomPage = String(currentPage - 1);
      link.setAttribute('aria-label', 'Prejšnja stran');
      link.appendChild(makeCaret());
      li.appendChild(link);
      list.appendChild(li);
    }

    const pages = new Set([1, totalPages]);
    for (let page = Math.max(1, currentPage - 2); page <= Math.min(totalPages, currentPage + 2); page += 1) {
      pages.add(page);
    }
    const pageNumbers = Array.from(pages).sort((a, b) => a - b);
    let previous = 0;
    pageNumbers.forEach((page) => {
      if (previous && page - previous > 1) appendEllipsis(list);
      appendPageLink(list, page, currentPage);
      previous = page;
    });

    if (currentPage < totalPages) {
      const li = document.createElement('li');
      const link = document.createElement('a');
      link.href = customPageUrl(currentPage + 1);
      link.className = 'pagination__item pagination__item--prev pagination__item-arrow link motion-reduce';
      link.dataset.kkCustomPage = String(currentPage + 1);
      link.setAttribute('aria-label', 'Naslednja stran');
      link.appendChild(makeCaret());
      li.appendChild(link);
      list.appendChild(li);
    }

    nav.appendChild(list);
    wrapper.appendChild(nav);
    container.appendChild(wrapper);
  }

  function renderResults(context, allItems, sortValue) {
    const gridContainer = document.getElementById('ProductGridContainer');
    const productGrid = gridContainer && gridContainer.querySelector('#product-grid');
    const list = productGrid && productGrid.querySelector('ul.product-grid');
    if (!productGrid || !list) return;

    const sorted = sortedCopy(allItems, sortValue);
    const totalPages = Math.max(1, Math.ceil(sorted.length / context.pageSize));
    const requestedPage = getCurrentCustomPage();
    const currentPage = Math.min(requestedPage, totalPages);

    if (currentPage !== requestedPage) {
      const params = new URLSearchParams(window.location.search);
      params.set('kk_page', String(currentPage));
      const searchParams = params.toString();
      history.replaceState({ searchParams }, '', buildStateUrl(params));
      syncFacetState(searchParams);
    }

    const start = (currentPage - 1) * context.pageSize;
    list.innerHTML = sorted.slice(start, start + context.pageSize).map((item) => item.html).join('');
    list.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });

    renderPagination(productGrid, currentPage, totalPages);
  }

  async function refresh(scrollToGrid = false) {
    updateControls();
    const sortValue = getCurrentCustomSort();
    if (!sortValue) return;

    const context = getFetchContext();
    if (!context || context.totalItems === 0) return;
    const sequence = ++refreshSequence;
    context.grid.classList.add('loading');

    try {
      const allItems = await fetchAllResults(context);
      if (sequence !== refreshSequence || getCurrentCustomSort() !== sortValue) return;
      renderResults(context, allItems, sortValue);
      if (scrollToGrid) {
        document.getElementById('product-grid')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch (error) {
      console.error('KupiKocke custom search sorting failed', error);
    } finally {
      if (sequence === refreshSequence) document.getElementById('product-grid')?.classList.remove('loading');
    }
  }

  document.addEventListener('click', (event) => {
    const pageLink = event.target.closest('[data-kk-custom-page]');
    if (!pageLink) return;
    event.preventDefault();
    const page = parseInt(pageLink.dataset.kkCustomPage || '1', 10) || 1;
    const params = new URLSearchParams(window.location.search);
    params.delete('page');
    params.set('kk_page', String(page));
    const searchParams = params.toString();
    history.pushState({ searchParams }, '', buildStateUrl(params));
    syncFacetState(searchParams);
    refresh(true);
  });

  const initialize = () => {
    if (!isSearchPage()) return;
    updateControls();
    refresh();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    requestAnimationFrame(initialize);
  }

  return {
    customSorts,
    labels,
    getCurrentCustomSort,
    setCustomSort,
    clearCustomSortState,
    normalizeSearchParams,
    updateControls,
    closeControl,
    refresh,
  };
})();

/* KupiKocke collection availability sorting, v0.3.45
 * Keeps Shopify's selected collection sort, but moves sold-out products
 * after all available products across the full filtered collection.
 */
window.KKCollectionAvailabilitySort = (() => {
  const resultCache = new Map();
  let refreshSequence = 0;

  function isCollectionPage() {
    return Boolean(
      document.querySelector('[data-kk-sort-context="collection"]') &&
      document.querySelector('#ProductGridContainer #product-grid[data-kk-total-items]')
    ) && !window.location.pathname.includes('/search');
  }

  function getCurrentPage() {
    const page = parseInt(new URLSearchParams(window.location.search).get('page') || '1', 10);
    return Number.isFinite(page) && page > 0 ? page : 1;
  }

  function getContext() {
    if (!isCollectionPage()) return null;
    const grid = document.querySelector('#ProductGridContainer #product-grid[data-kk-total-items]');
    const sectionId = grid?.dataset.id;
    const totalItems = parseInt(grid?.dataset.kkTotalItems || '0', 10);
    const pageSize = parseInt(grid?.dataset.kkPageSize || '16', 10) || 16;
    const maxPages = parseInt(grid?.dataset.kkSortMaxPages || '0', 10) || 0;
    if (!grid || !sectionId || !Number.isFinite(totalItems) || totalItems <= 0) return null;

    const baseParams = new URLSearchParams(window.location.search);
    baseParams.delete('page');
    baseParams.delete('section_id');

    const cacheParams = new URLSearchParams(baseParams);
    const cacheKey = `${window.location.pathname}?${cacheParams.toString()}|${sectionId}|${totalItems}|${pageSize}`;

    return {
      grid,
      sectionId,
      totalItems,
      pageSize,
      totalPages: Math.max(1, Math.min(Math.ceil(totalItems / pageSize), maxPages || Infinity)),
      maxItems: Math.min(totalItems, (maxPages || Math.ceil(totalItems / pageSize)) * pageSize),
      baseParams,
      cacheKey,
    };
  }

  async function fetchPage(context, pageNumber) {
    const params = new URLSearchParams(context.baseParams);
    params.set('section_id', context.sectionId);
    params.set('page', String(pageNumber));
    const response = await fetch(`${window.location.pathname}?${params.toString()}`);
    if (!response.ok) throw new Error(`Collection availability sort fetch failed: ${response.status}`);

    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    // A product sold out for 30+ days renders an empty <li> because the
    // product-card snippet suppresses its contents. Exclude that placeholder
    // before sorting/paginating, otherwise it occupies a blank grid cell.
    return Array.from(parsed.querySelectorAll('#ProductGridContainer #product-grid ul > li[data-kk-sort-id]'))
      .filter((item) => item.querySelector('.kk-card'))
      .map((item) => ({
        id: item.dataset.kkSortId || '',
        available: item.dataset.kkSortAvailable === '1',
        html: item.outerHTML,
      }));
  }

  async function fetchAllResults(context) {
    if (resultCache.has(context.cacheKey)) return resultCache.get(context.cacheKey);

    const all = [];
    const concurrency = 4;
    for (let start = 1; start <= context.totalPages; start += concurrency) {
      const requests = [];
      for (let page = start; page < start + concurrency && page <= context.totalPages; page += 1) {
        requests.push(fetchPage(context, page));
      }
      const batches = await Promise.all(requests);
      batches.forEach((batch) => all.push(...batch));
    }

    const unique = [];
    const seen = new Set();
    all.forEach((item) => {
      if (!item.id || seen.has(item.id)) return;
      seen.add(item.id);
      unique.push(item);
    });

    resultCache.set(context.cacheKey, unique);
    return unique;
  }

  function sortAvailableLast(items) {
    const available = [];
    const soldOut = [];
    items.forEach((item) => (item.available ? available : soldOut).push(item));
    return available.concat(soldOut);
  }

  function render(context, items) {
    const list = context.grid.querySelector('ul.kk-collection-grid__list, ul.product-grid');
    if (!list) return;

    const sorted = sortAvailableLast(items).slice(0, context.maxItems);
    const currentPage = Math.min(getCurrentPage(), Math.max(1, Math.ceil(sorted.length / context.pageSize)));
    const start = (currentPage - 1) * context.pageSize;
    list.innerHTML = sorted.slice(start, start + context.pageSize).map((item) => item.html).join('');

    // Rebuilt cards must not inherit stale scroll-animation states from fetched pages.
    // Otherwise an invisible card can occupy a grid cell and leave a visible gap.
    list.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
      element.classList.remove('scroll-trigger--offscreen', 'animate--slide-in', 'animate--fade-in');
      element.style.opacity = '1';
      element.style.transform = 'none';
      element.style.animation = 'none';
    });
  }

  async function refresh() {
    const context = getContext();
    if (!context) return;

    const sequence = ++refreshSequence;
    context.grid.classList.add('loading');
    try {
      const allItems = await fetchAllResults(context);
      if (sequence !== refreshSequence) return;
      render(context, allItems);
    } catch (error) {
      console.error('KupiKocke collection availability sorting failed', error);
    } finally {
      if (sequence === refreshSequence) context.grid.classList.remove('loading');
    }
  }

  const initialize = () => refresh();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    requestAnimationFrame(initialize);
  }

  return { refresh };
})();

/* KupiKocke custom sorting logic superseded by v0.3.35 */
/* KupiKocke sort dropdown click isolation, v0.3.35 */
if (!window.kkSortDropdownCaptureBound) {
  window.kkSortDropdownCaptureBound = true;

  document.addEventListener('click', function (event) {
    const trigger = event.target.closest('.kk-sort-trigger');
    const option = event.target.closest('.kk-sort-option');

    if (!trigger && !option) return;

    event.preventDefault();
    event.stopPropagation();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();

    if (trigger) {
      const wrapper = trigger.closest('.kk-sort-select');
      const menu = wrapper && wrapper.querySelector('.kk-sort-menu');
      if (!wrapper || !menu) return;

      const opening = menu.hidden;

      document.querySelectorAll('.kk-sort-select.is-open').forEach(function (other) {
        if (other !== wrapper) {
          other.classList.remove('is-open');
          const otherMenu = other.querySelector('.kk-sort-menu');
          const otherTrigger = other.querySelector('.kk-sort-trigger');
          if (otherMenu) otherMenu.hidden = true;
          if (otherTrigger) otherTrigger.setAttribute('aria-expanded', 'false');
        }
      });

      menu.hidden = !opening;
      wrapper.classList.toggle('is-open', opening);
      trigger.setAttribute('aria-expanded', opening ? 'true' : 'false');
      return;
    }

    if (option) {
      const wrapper = option.closest('.kk-sort-select');
      const select = wrapper && wrapper.querySelector('.kk-sort-native');
      if (!wrapper || !select) return;

      if (option.dataset.kkCustomSearchSort === 'true' && window.KKSearchCustomSort) {
        window.KKSearchCustomSort.closeControl(wrapper);
        window.KKSearchCustomSort.setCustomSort(option.dataset.value);
        return;
      }

      const hadCustomSearchSort =
        wrapper.hasAttribute('data-kk-search-sort-control') &&
        window.KKSearchCustomSort &&
        Boolean(window.KKSearchCustomSort.getCurrentCustomSort());

      if (wrapper.hasAttribute('data-kk-search-sort-control') && window.KKSearchCustomSort) {
        window.KKSearchCustomSort.clearCustomSortState();
      }

      if (hadCustomSearchSort || select.value !== option.dataset.value) {
        select.value = option.dataset.value;

        wrapper.querySelectorAll('.kk-sort-option').forEach(function (item) {
          const selected = item === option;
          item.classList.toggle('is-selected', selected);
          item.setAttribute('aria-selected', selected ? 'true' : 'false');
        });

        const label = wrapper.querySelector('.kk-sort-trigger__label');
        if (label) label.textContent = option.textContent.trim();

        wrapper.classList.remove('is-open');
        const menu = wrapper.querySelector('.kk-sort-menu');
        const button = wrapper.querySelector('.kk-sort-trigger');
        if (menu) menu.hidden = true;
        if (button) button.setAttribute('aria-expanded', 'false');

        select.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  }, true);
}
